
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ExchangeSelector } from './ExchangeSelector';
import { ColorSchemeSelector } from './ColorSchemeSelector';
import { deployBotSchema, DeployBotFormValues } from '@/types/bot-forms';
import { ExchangePlatform, ColorScheme } from '@/types/trader';

interface BotFormProps {
  onSubmit: (data: DeployBotFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export const BotForm = ({ onSubmit, onCancel, isSubmitting }: BotFormProps) => {
  const [selectedExchanges, setSelectedExchanges] = React.useState<ExchangePlatform[]>([]);

  const form = useForm<DeployBotFormValues>({
    resolver: zodResolver(deployBotSchema),
    defaultValues: {
      name: '',
      description: '',
      exchanges: [],
      colorScheme: 'royal_purple',
    },
  });

  // Available exchanges
  const exchanges: ExchangePlatform[] = [
    'Ctrader', 'MT4', 'MT5', 'Binance', 'OKX', 'BingX', 'Bybit', 'Bitget'
  ];

  // Available color schemes
  const colorSchemes: { value: ColorScheme | string; label: string }[] = [
    { value: 'royal_purple', label: 'Royal Purple' },
    { value: 'ocean_blue', label: 'Ocean Blue' },
    { value: 'emerald_green', label: 'Emerald Green' },
    { value: 'ruby_red', label: 'Ruby Red' },
    { value: 'amber_gold', label: 'Amber Gold' },
    { value: 'midnight_black', label: 'Midnight Black' },
    { value: 'coral_orange', label: 'Coral Orange' },
  ];

  const handleExchangeToggle = (exchange: ExchangePlatform) => {
    setSelectedExchanges(prevSelected => {
      const isAlreadySelected = prevSelected.includes(exchange);
      
      if (isAlreadySelected) {
        // Remove the exchange if it's already selected
        const newSelected = prevSelected.filter(item => item !== exchange);
        form.setValue('exchanges', newSelected);
        return newSelected;
      } else {
        // Add the exchange if it's not already selected
        const newSelected = [...prevSelected, exchange];
        form.setValue('exchanges', newSelected);
        return newSelected;
      }
    });
  };

  const handleFormSubmit = form.handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleFormSubmit} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bot Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter a name for your bot" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe what your bot does" 
                  className="resize-none" 
                  rows={3} 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="exchanges"
          render={() => (
            <ExchangeSelector
              exchanges={exchanges}
              selectedExchanges={selectedExchanges}
              onToggleExchange={handleExchangeToggle}
              formErrors={form.formState.errors.exchanges}
            />
          )}
        />

        <FormField
          control={form.control}
          name="colorScheme"
          render={({ field }) => (
            <ColorSchemeSelector
              colorSchemes={colorSchemes}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? 'Deploying...' : 'Deploy Bot'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
